import {
    signalStore,
    withFeature,
    withHooks,
    withMethods,
    withState,
} from '@ngrx/signals';
import { environment } from '@dxp/core/environment';
import { IWrapState } from '@dxp/core/models';
import { updateState } from '@angular-architects/ngrx-toolkit';
import { DestroyRef, inject } from '@angular/core';
import { tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { withWorkItems, WORKITEMS_STORE } from '../workitems';

interface IWrapStore {
    code: Record<string, string>;
    notes: Record<string, string>;
    summary: Record<string, boolean>;
    noSummaryMessage: Record<string, string>;
}

const initialState: IWrapStore = {
    code: {},
    notes: {},
    summary: {},
    noSummaryMessage: {},
};

export const WrapStore = signalStore(
    { providedIn: 'root' },
    environment.storeWithDevTools('WrapStore'),
    withFeature(() => withWorkItems(inject(WORKITEMS_STORE))),
    withState(initialState),
    withMethods(store => ({
        getWrapByWorkItemId(workitemId: string): IWrapState {
            return {
                code: store.code()[workitemId] ?? '',
                notes: store.notes()[workitemId] ?? '',
                summary: store.summary()[workitemId] ?? false,
                noSummaryMessage: store.noSummaryMessage()[workitemId] ?? '',
            };
        },
        updateWrap(
            workitemId: string,
            code: string,
            notes: string,
            summary: boolean,
            noSummaryMessage: string,
        ) {
            if (!store.workItems().some(w => w.workItemId == workitemId))
                return;

            updateState(store, 'UPDATE_WRAP', state => ({
                ...state,
                code: { ...state.code, [workitemId]: code },
                notes: { ...state.notes, [workitemId]: notes },
                summary: { ...state.summary, [workitemId]: summary },
                noSummaryMessage: {
                    ...state.noSummaryMessage,
                    [workitemId]: noSummaryMessage,
                },
            }));
        },
        clearWrap(workitemId: string): void {
            updateState(store, 'CLEAR_WRAP', state => {
                const { [workitemId]: _code, ...remainingCode } = state.code;
                const { [workitemId]: _notes, ...remainingNotes } = state.notes;
                const { [workitemId]: _summary, ...remainingSummary } =
                    state.summary;
                const {
                    [workitemId]: _noSummaryMessage,
                    ...remainingNoSummaryMessage
                } = state.noSummaryMessage;

                return {
                    code: remainingCode,
                    notes: remainingNotes,
                    summary: remainingSummary,
                    noSummaryMessage: remainingNoSummaryMessage,
                };
            });
        },
    })),
    withHooks({
        onInit(store, destroyRef = inject(DestroyRef)) {
            store.onWorkItemRemoved$
                .pipe(
                    tap(workitem => store.clearWrap(workitem.workItemId)),
                    takeUntilDestroyed(destroyRef),
                )
                .subscribe();
        },
    }),
);
